// App.js
import React from "react";
import { BrowserRouter as Router, Routes, Route, useParams, useNavigate } from "react-router-dom";
import { Analytics } from "@vercel/analytics/react";

/* Público */
import Home from "./screens/HomeScreen";
import Login from "./screens/LoginScreen";
import NewsDetail from "./screens/NewsDetail";
import CategoryPage from "./screens/Categories";

/* Admin */
import HomeAdmin from "./screens/admin/HomeAdmin.module";
import NewsManager from "./screens/admin/NewsManager";
import CreateAuthors from "./screens/admin/CreateAuthors.module";
import CreateCategories from "./screens/admin/CreateCategories";
import CreateImages from "./screens/admin/CreateImages";
import NewsTodo from "./screens/admin/NewsTodo";
import CreateTags from "./screens/admin/CreateTags";

/* Componente de protección */
import ProtectedRoute from "./screens/admin/ProtectedRoute";

/**
 * NewsRouter
 * - Si el param es numérico: busca /api/news/:id y redirige al slug canónico.
 * - Si el param NO es numérico: renderiza NewsDetail (que tratará al param como slug).
 */
function NewsRouter() {
  const { param } = useParams(); // puede ser id o slug
  const navigate = useNavigate();
  const API_BASE = process.env.REACT_APP_API_BASE || "https://backend-zonamicroondas.vercel.app/api";

  React.useEffect(() => {
    let mounted = true;

    const checkAndRedirect = async () => {
      if (!param) {
        navigate("/", { replace: true });
        return;
      }

      // Si es un entero puro (ej. "22") -> tratar como ID antiguo
      if (/^\d+$/.test(param)) {
        try {
          const res = await fetch(`${API_BASE}/news/${encodeURIComponent(param)}`);
          const json = await res.json();

          if (!mounted) return;

          if (json && json.success && json.data) {
            const news = json.data;
            const slug = news.canonical_slug;
            // category_slug puede venir en diferentes propiedades según tu backend
            const categorySlug = news.category_slug || news.categories?.slug || news.category_slug || '';

            if (slug) {
              if (categorySlug) {
                navigate(`/${encodeURIComponent(categorySlug)}/articulos/${encodeURIComponent(slug)}`, { replace: true });
              } else {
                navigate(`/news/${encodeURIComponent(slug)}`, { replace: true });
              }
              return;
            }
          }

          // Si no encontramos la noticia por id -> enviar al home (o puedes mostrar 404)
          navigate("/", { replace: true });
        } catch (err) {
          console.error("NewsRouter redirect error:", err);
          navigate("/", { replace: true });
        }
      }
      // Si no es numérico, NO hacemos nada: NewsDetail se encargará de usar el slug
    };

    checkAndRedirect();

    return () => { mounted = false; };
  }, [param, navigate, API_BASE]);

  // Mientras potencialmente redirige mostramos algo mínimo si es ID.
  if (/^\d+$/.test(param)) {
    return <div style={{ padding: 40, textAlign: "center" }}>Redirigiendo...</div>;
  }

  // Si no es numérico, renderizamos el componente de detalle (usa useParams en su interior).
  return <NewsDetail />;
}

function App() {
  return (
    <Router>
      <Analytics />
      <Routes>
        {/* ========== RUTAS PÚBLICAS ========== */}
        <Route path="/" element={<Home />} />

        {/* Ruta canonical con categoría + slug */}
        <Route path="/:categorySlug/articulos/:slug" element={<NewsDetail />} />

        {/* Ruta única para news: param puede ser slug o id antiguo */}
        <Route path="/news/:param" element={<NewsRouter />} />

        {/* Página de categoría */}
        <Route path="/categoria/:slug" element={<CategoryPage />} />

        <Route path="/login" element={<Login />} />

        {/* ========== RUTAS PROTEGIDAS - ADMIN ========== */}
        <Route
          path="/admin"
          element={
            <ProtectedRoute>
              <HomeAdmin />
            </ProtectedRoute>
          }
        />

        <Route
          path="/admin/news/create"
          element={
            <ProtectedRoute>
              <NewsManager />
            </ProtectedRoute>
          }
        />

        <Route
          path="/admin/news/todos"
          element={
            <ProtectedRoute>
              <NewsTodo />
            </ProtectedRoute>
          }
        />

        <Route
          path="/admin/authors"
          element={
            <ProtectedRoute>
              <CreateAuthors />
            </ProtectedRoute>
          }
        />

        <Route
          path="/admin/categories/create"
          element={
            <ProtectedRoute>
              <CreateCategories />
            </ProtectedRoute>
          }
        />

        <Route
          path="/admin/images"
          element={
            <ProtectedRoute>
              <CreateImages />
            </ProtectedRoute>
          }
        />

        <Route
          path="/admin/tags"
          element={
            <ProtectedRoute>
              <CreateTags />
            </ProtectedRoute>
          }
        />

        {/* Ruta catch-all para URLs no encontradas */}
        <Route path="*" element={<Home />} />
      </Routes>
    </Router>
  );
}

export default App;
